import React, { useMemo } from "react";

import ComponentLoader from "../../../components/Spinner/ComponentLoader";
import MeetingAccordionContent from "../../../pages/Meetings/WorkspaceDetails/components/Meetings/MeetingAccordionContent";
import { MeetingProvider } from "../../../pages/Meetings/WorkspaceDetails/components/Meetings/MeetingContext";
import MeetingModalHeader from "../../../pages/Meetings/WorkspaceDetails/components/Meetings/MeetingModalHeader";

import { useCurrentUser, useMeetingDetails } from "../../../api/hooks";

interface MeetingModalContentProps {
	meetingId: string;
}

const MeetingModalContent = ({ meetingId }: MeetingModalContentProps) => {
	const { data: currentUser } = useCurrentUser();

	const { meeting, workspace, isLoading: isFetchingData, error: meetingError } = useMeetingDetails(meetingId);

	const userId = currentUser?.me?.authorization?.payload.user_id || "";

	// Create context value for MeetingProvider
	const contextValue = useMemo(() => {
		if (!meeting || !workspace || !userId) return null;

		const meetingCreatorId = meeting.created?.userId;
		const otherParticipantId = meeting.participants?.find((p) => p.userId !== meetingCreatorId)?.userId;

		// Use the "other" participant (not the creator) since this is always admin view
		const participantId = otherParticipantId;
		const participantName = workspace?.participants?.find((p) => p.userId === participantId)?.name;

		return {
			meeting,
			userId,
			isExpanded: true, // Always expanded in modal
			toggleAccordion: () => {}, // No-op in modal
			setEditingMeeting: () => {}, // Disabled for admin
			setUserAssignment: () => {}, // Disabled for admin
			participantId,
			participantName,
			workspace,
			isInteractionDisabled: true, // Always disabled for admin view
			isAdminView: true, // Always admin view from company meetings
		};
	}, [meeting, workspace, userId]);

	if (isFetchingData) {
		return (
			<div className="flex h-[660px] w-[1312px] items-center justify-center">
				<ComponentLoader />
			</div>
		);
	}

	if (meetingError || !meeting || !contextValue) {
		return null;
	}

	return (
		<MeetingProvider value={contextValue}>
			<div className="flex h-[660px] w-[1312px] flex-col">
				<MeetingModalHeader />
				<MeetingAccordionContent />
			</div>
		</MeetingProvider>
	);
};

export default MeetingModalContent;
